import { Skeleton } from '@/components/ui/skeleton';
import { getEntityQueryOptions } from '@/queries/getEntityQueryOptions';
import { updateEntity } from '@/queries/updateEntityMutation';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'
import type { DataSource } from '@/models/data-source';
import type { DataSourceParameter } from '@/models/data-source-parameter';
import { DataSourcesManager } from '@/components/filter-builder/datasources-manager';

export const Route = createFileRoute(
  '/_app/$module_id/$entity_type_id/$entity_id/_tabs/data-sources/',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { entity_type_id, entity_id } = Route.useParams();
  const { data: result, isLoading, refetch } = useQuery(getEntityQueryOptions(entity_type_id, entity_id));

  if (isLoading) {
    return <>
      <Skeleton className="h-10 w-1/3 mb-4" />
      <Skeleton className="h-10 w-full mb-4" />
      <Skeleton className="h-10 w-full mb-4" />
      <Skeleton className="h-10 w-full mb-4" />
    </>
  }
  return <EntityDataSources
    entity={result?.entity as IMergableDocument}
    entity_type_id={entity_type_id}
    onUpdate={refetch}
  />;
}

export interface IMergableDocument {
  createDate?: string;
  createdByUserId?: string;
  customerId?: string;
  dataSources?: Array<DataSource>;
  distinctImportId?: string;
  fileName?: string;
  id?: string;
  name: string;
  parameters?: Array<DataSourceParameter>;
  showOnActions?: boolean;
  targetCollection: string;
  updateDate?: string;
  entityDefinitionId?: string;
  updatedByUserId?: string;
}

function EntityDataSources({
  entity,
  entity_type_id,
  onUpdate
}: {
  entity: IMergableDocument,
  entity_type_id: string,
  onUpdate: () => void
}) {
  const updateMutation = updateEntity();

  const handleDataSourcesSave = async (newDataSources: DataSource[]) => {
    if (!entity.id) return;

    try {
      await updateMutation.mutateAsync({
        entityType: entity_type_id,
        entityId: entity.id,
        updates: {
          dataSources: newDataSources
        }
      });
      onUpdate();
    } catch (error) {
      console.error('Failed to save data sources:', error);
    }
  };

  if (!entity.targetCollection && entity_type_id !== 'reports') {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-sm">No target collection configured for this entity.</div>
      </div>
    );
  }

  return (
    <DataSourcesManager
      dataSources={entity.dataSources || []}
      onSave={handleDataSourcesSave}
      entity_type_id={entity_type_id}
      targetCollection={entity.targetCollection}
    />
  );
}

